import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, Button, Alert, Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';

import { INVOICE_ORDER_EVENT, parseInvoiceOrderUrl } from '../../utils/invoiceOrder';
import { loadTurnstile, postContact, TURNSTILE_SITEKEY } from '../../utils/turnstile';

// Laskulla tilaaminen: tilausyhteenveto + laskutustiedot → sähköposti myynnille tendera.fi:n lomakerajapinnan kautta.
// Korvaa HubSpotin "Order form / Invoice" -lomakkeet (fi/en/sv). Myyjä laskuttaa ja aktivoi tunnukset kuten ennenkin.
const INTERVAL_KEY = { YEARLY: 'yearly', SEMIANNUAL: 'semiannual', SIX_MONTHS: 'semiannual', MONTHLY: 'monthly' };

const InvoiceOrderModal = () => {
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();
    const [data, setData] = useState(null); // { order, contact }
    const [state, setState] = useState('idle'); // idle | sending | sent | error
    const [token, setToken] = useState('');
    const widgetRef = useRef(null);
    const widgetId = useRef(null);

    useEffect(() => {
        const onOpen = (e) => {
            try { setData(parseInvoiceOrderUrl(e.detail.url)); setState('idle'); setToken(''); widgetId.current = null; }
            catch (err) { console.error('invoice order url', err); }
        };
        window.addEventListener(INVOICE_ORDER_EVENT, onOpen);
        return () => window.removeEventListener(INVOICE_ORDER_EVENT, onOpen);
    }, []);

    useEffect(() => {
        if (!data) return undefined;
        form.setFieldsValue(data.contact);
        let cancelled = false;
        loadTurnstile().then((ts) => {
            if (cancelled || !widgetRef.current || widgetId.current !== null) return;
            widgetId.current = ts.render(widgetRef.current, {
                sitekey: TURNSTILE_SITEKEY, language: (i18n.language || 'fi').split('-')[0], appearance: 'interaction-only',
                callback: (tok) => setToken(tok), 'expired-callback': () => setToken(''), 'error-callback': () => setToken(''),
            });
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [data, form, i18n.language]);

    const close = () => setData(null);
    if (!data) return null;
    const { order } = data;
    const interval = t(`invoiceOrder.interval.${INTERVAL_KEY[order.billingInterval] || 'yearly'}`);
    const priceLine = order.price ? `${order.price} ${order.currency} / ${interval}` : interval;

    const submit = async (values) => {
        setState('sending');
        const lines = [
            `TILAUS / ORDER`,
            `Paketti: ${order.planName} (id ${order.planId || '-'})`,
            `Laskutusjakso: ${order.billingInterval}`,
            `Hinta: ${order.price || '-'} ${order.currency} / jakso · kk-hinta ${order.monthlyPrice || '-'} · vuosihinta ${order.yearlyPrice || '-'}`,
            `Käyttäjäpaikat: ${order.maxUsers || '-'} + lisäpaikat ${order.extraSeats}`,
            `Organisaatio-id: ${data.contact.organizationId || '-'}`,
            Object.keys(order.upgrade).length ? `Upgrade-konteksti: ${JSON.stringify(order.upgrade)}` : null,
            ``,
            `LASKUTUSTIEDOT`,
            `Yritys: ${values.company || ''}`,
            `Y-tunnus: ${values.businessId || ''}`,
            `Yhteyshenkilö: ${values.name || ''} · ${values.email || ''} · ${values.phone || ''}`,
            `Osoite: ${values.address || ''}, ${values.zip || ''} ${values.city || ''} ${values.country || ''}`,
            `Laskutusviite / verkkolasku: ${values.reference || '-'}`,
            values.notes ? `` : null,
            values.notes ? `Lisätiedot: ${values.notes}` : null,
            ``,
            `Kieli: ${i18n.language} · Sivu: app:${window.location.pathname}`,
        ].filter((l) => l !== null).join('\n');
        try {
            await postContact({
                name: values.name, email: values.email, company: values.company || '', message: lines,
                topic: 'laskutilaus', page: `app:${window.location.pathname}`, utm: `plan=${order.planName}&interval=${order.billingInterval}&seats=${order.extraSeats}`,
                'cf-turnstile-response': token,
            });
            setState('sent');
        } catch (e) {
            setState('error');
            try { window.turnstile?.reset(widgetId.current); } catch (x) { /* noop */ }
            setToken('');
        }
    };

    return (
        <Modal open onCancel={close} footer={null} width="min(640px, 92vw)" centered destroyOnHidden
            title={t('invoiceOrder.title', 'Tilaa laskulla')}
            styles={{ content: { borderRadius: 16 }, header: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}>
            {state === 'sent' ? (
                <Alert type="success" showIcon message={t('invoiceOrder.sentTitle', 'Kiitos tilauksesta.')}
                    description={t('invoiceOrder.sentBody', 'Tarkistamme tilauksen ja aktivoimme paketin yhden arkipäivän sisällä. Lasku lähetetään antamaasi osoitteeseen.')} />
            ) : (
                <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
                    <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
                        <Descriptions.Item label={t('invoiceOrder.plan', 'Paketti')}>{order.planName}</Descriptions.Item>
                        <Descriptions.Item label={t('invoiceOrder.price', 'Hinta')}>{priceLine} {t('invoiceOrder.vat', '+ alv')}</Descriptions.Item>
                        {Number(order.extraSeats) > 0 && (
                            <Descriptions.Item label={t('invoiceOrder.extraSeats', 'Lisäkäyttäjäpaikat')}>{order.extraSeats}</Descriptions.Item>
                        )}
                    </Descriptions>
                    <p style={{ marginTop: 0, color: '#5f6d78' }}>{t('invoiceOrder.lead', 'Tarkista laskutustiedot. Tilaus menee Tenderan myynnille, joka aktivoi paketin ja lähettää laskun.')}</p>
                    <Form.Item name="company" label={t('invoiceOrder.company', 'Yritys')} rules={[{ required: true, message: t('invoiceOrder.required', 'Pakollinen tieto') }]}>
                        <Input maxLength={160} autoComplete="organization" />
                    </Form.Item>
                    <Form.Item name="businessId" label={t('invoiceOrder.businessId', 'Y-tunnus')} rules={[{ required: true, message: t('invoiceOrder.required', 'Pakollinen tieto') }]}>
                        <Input maxLength={40} />
                    </Form.Item>
                    <Form.Item name="name" label={t('invoiceOrder.name', 'Yhteyshenkilö')} rules={[{ required: true, message: t('invoiceOrder.required', 'Pakollinen tieto') }]}>
                        <Input maxLength={120} autoComplete="name" />
                    </Form.Item>
                    <Form.Item name="email" label={t('invoiceOrder.email', 'Sähköposti (lasku ja vahvistus)')} rules={[{ required: true, type: 'email', message: t('invoiceOrder.emailInvalid', 'Tarkista sähköpostiosoite') }]}>
                        <Input maxLength={200} autoComplete="email" />
                    </Form.Item>
                    <Form.Item name="phone" label={t('invoiceOrder.phone', 'Puhelin')}>
                        <Input maxLength={40} autoComplete="tel" />
                    </Form.Item>
                    <Form.Item name="address" label={t('invoiceOrder.address', 'Laskutusosoite')}>
                        <Input maxLength={200} autoComplete="street-address" />
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                        <Form.Item name="zip" label={t('invoiceOrder.zip', 'Postinumero')}><Input maxLength={12} autoComplete="postal-code" /></Form.Item>
                        <Form.Item name="city" label={t('invoiceOrder.city', 'Postitoimipaikka')}><Input maxLength={80} autoComplete="address-level2" /></Form.Item>
                    </div>
                    <Form.Item name="country" hidden><Input /></Form.Item>
                    <Form.Item name="reference" label={t('invoiceOrder.reference', 'Laskutusviite tai verkkolaskuosoite (valinnainen)')}>
                        <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item name="notes" label={t('invoiceOrder.notes', 'Lisätiedot (valinnainen)')}>
                        <Input.TextArea rows={3} maxLength={1500} />
                    </Form.Item>
                    <div ref={widgetRef} style={{ marginBottom: 12 }} />
                    {state === 'error' && (
                        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={t('invoiceOrder.errorTitle', 'Lähetys ei onnistunut.')}
                            description={t('invoiceOrder.errorBody', 'Yritä hetken kuluttua uudelleen tai kirjoita osoitteeseen support@tendera.bid.')} />
                    )}
                    <Button type="primary" htmlType="submit" loading={state === 'sending'} disabled={!token} block>
                        {t('invoiceOrder.send', 'Lähetä tilaus')}
                    </Button>
                </Form>
            )}
        </Modal>
    );
};

export default InvoiceOrderModal;
